import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnInit,
    ViewEncapsulation,
    input,
    output,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ReportingFieldInfo, AnswerType } from "./data.ingestion.service";

export interface AggregationSpec {
    type: "COUNT" | "COUNT_DISTINCT" | "SUM" | "AVG";
    field: string;
}

@TypeManagerDecorator("s25-ng-aggregation-selector")
@Component({
    selector: "s25-ng-aggregation-selector",
    template: `
        <div class="aggregation-selector">
            <div class="aggregation-list">
                @for (aggregation of selectedAggregations(); track $index; let i = $index) {
                    <div class="aggregation-item">
                        <div class="aggregation-controls">
                            <select
                                [(ngModel)]="aggregation.type"
                                (change)="onAggregationTypeChanged(aggregation, $event)"
                            >
                                <option value="COUNT">Count (*)</option>
                                <option value="COUNT_DISTINCT">Count (Distinct)</option>
                                <option value="SUM">Sum</option>
                                <option value="AVG">Average</option>
                            </select>

                            <select
                                [(ngModel)]="aggregation.field"
                                (change)="onAggregationChanged()"
                                [disabled]="aggregation.type === 'COUNT'"
                            >
                                @if (aggregation.type === "COUNT") {
                                    <option value="*">All Records</option>
                                } @else if (aggregation.type === "COUNT_DISTINCT") {
                                    <option value="cont_id">Distinct Contacts</option>
                                }
                                @for (
                                    field of getAvailableFieldsForAggregation(aggregation.type);
                                    track field.reportingFieldCode
                                ) {
                                    <option [value]="field.reportingFieldCode">
                                        {{ field.reportingFieldCode }}
                                    </option>
                                }
                                <!-- Note: form is intentionally excluded from aggregations (only available for groupBy) -->
                            </select>

                            @if (aggregation.type !== "COUNT" && (!aggregation.field || aggregation.field === "")) {
                                <span class="field-required" title="Please select a field for this aggregation type">
                                    ⚠️ Field Required
                                </span>
                            } @else if (!isFieldValidForAggregationType(aggregation.field, aggregation.type)) {
                                <span
                                    class="field-warning"
                                    title="Current field is not valid for this aggregation type"
                                >
                                    ⚠️ Invalid Field
                                </span>
                            }

                            <button class="btn btn-sm btn-secondary" (click)="clearAggregation(i)">Clear</button>
                            <button class="btn btn-sm btn-danger" (click)="removeAggregation(i)">Remove</button>
                        </div>
                    </div>
                }
            </div>

            <div class="add-aggregation">
                <button class="btn btn-primary" (click)="addAggregation()">Add Aggregation</button>
            </div>

            @if (validationError()) {
                <div class="validation-error">
                    {{ validationError() }}
                </div>
            }

            <div class="aggregation-help">
                <h5>Aggregation Types:</h5>
                <ul>
                    <li><strong>Count (*):</strong> Count all submissions</li>
                    <li><strong>Count (Distinct):</strong> Count distinct values for the selected field</li>
                    <li><strong>Sum:</strong> Sum numeric values (only available for numeric fields)</li>
                    <li><strong>Average:</strong> Average numeric values (only available for numeric fields)</li>
                </ul>
            </div>

            @if (selectedAggregations().length === 0) {
                <div class="no-aggregations">
                    <p>No aggregations selected. Click "Add Aggregation" to get started.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .aggregation-selector {
            padding: 15px;
        }

        .aggregation-list {
            margin-bottom: 20px;
        }

        .aggregation-item {
            margin-bottom: 15px;
            padding: 15px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--surface-2-bg);
        }

        .aggregation-controls {
            display: flex;
            gap: 10px;
            align-items: center;
        }

        .aggregation-controls select {
            padding: 5px 10px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .btn {
            padding: 5px 10px;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            color: var(--color--text);
            cursor: pointer;
            border-radius: 4px;
        }

        .btn-sm {
            font-size: 0.875em;
        }

        .btn-primary {
            background: var(--color--link-text);
            color: white;
            border-color: var(--color--link-text);
        }

        .btn-danger {
            background: var(--color--danger);
            color: white;
            border-color: var(--color--danger);
        }

        .btn-secondary {
            background: var(--color--text-muted);
            color: white;
            border-color: var(--color--text-muted);
        }

        .btn:hover {
            opacity: 0.8;
        }

        .field-warning {
            color: var(--color--warning);
            font-size: 1.2em;
            margin-left: 5px;
            cursor: help;
        }

        .field-required {
            color: var(--color--danger);
            font-size: 0.9em;
            margin-left: 5px;
            font-weight: bold;
        }

        .add-aggregation {
            margin-bottom: 20px;
        }

        .validation-error {
            background: var(--color--danger);
            color: white;
            border: 1px solid var(--color--danger);
            border-radius: 4px;
            padding: 10px 15px;
            margin-bottom: 20px;
            font-size: 0.9em;
        }

        .aggregation-help {
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--link-text);
            border-radius: 4px;
            padding: 15px;
            margin-bottom: 20px;
        }

        .aggregation-help h5 {
            margin: 0 0 10px 0;
            color: var(--color--link-text);
        }

        .aggregation-help ul {
            margin: 0;
            padding-left: 20px;
        }

        .aggregation-help li {
            margin-bottom: 5px;
        }

        .no-aggregations {
            padding: 20px;
            text-align: center;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionAggregationSelectorComponent implements OnInit {
    availableFields = input<ReportingFieldInfo[]>([]);
    selectedAggregations = input<AggregationSpec[]>([]);
    aggregationsChanged = output<AggregationSpec[]>();

    // Expose enum to template
    AnswerType = AnswerType;

    // Validation error tracking
    validationError = signal<string | null>(null);

    constructor(
        private elem: ElementRef,
        private cdRef: ChangeDetectorRef,
        private z: NgZone,
    ) {}

    ngOnInit() {}

    getAvailableFieldsForAggregation(aggregationType: string): ReportingFieldInfo[] {
        switch (aggregationType) {
            case "SUM":
            case "AVG":
                // Only numeric fields for SUM/AVG
                return this.availableFields().filter(
                    (field) => field.answerType === AnswerType.INTEGER || field.answerType === AnswerType.FLOAT,
                );
            case "COUNT_DISTINCT":
                // All fields except cont_id (which is handled separately)
                return this.availableFields();
            case "COUNT":
                // COUNT(*) doesn't need a field
                return [];
            default:
                return this.availableFields();
        }
    }

    addAggregation() {
        const newAggregation: AggregationSpec = {
            type: "COUNT",
            field: "*", // COUNT(*) doesn't need a specific field
        };
        const newAggregations = [...this.selectedAggregations(), newAggregation];
        this.aggregationsChanged.emit(newAggregations);
    }

    removeAggregation(index: number) {
        const newAggregations = this.selectedAggregations().filter((_, i) => i !== index);
        this.aggregationsChanged.emit(newAggregations);
    }

    onAggregationTypeChanged(aggregation: AggregationSpec, event: Event) {
        // When aggregation type changes, set appropriate default field
        switch (aggregation.type) {
            case "COUNT":
                aggregation.field = "*";
                break;
            case "COUNT_DISTINCT":
                aggregation.field = "cont_id"; // Default to distinct contacts
                break;
            case "SUM":
            case "AVG":
                // Set to first available numeric field, or empty if none
                const numericFields = this.getAvailableFieldsForAggregation(aggregation.type);
                aggregation.field = numericFields.length > 0 ? numericFields[0].reportingFieldCode : "";
                break;
        }

        this.aggregationsChanged.emit(this.selectedAggregations());
    }

    onAggregationChanged() {
        this.aggregationsChanged.emit(this.selectedAggregations());
    }

    clearAggregation(index: number) {
        const aggregations = [...this.selectedAggregations()];
        aggregations[index] = {
            type: "COUNT",
            field: "*",
        };
        this.aggregationsChanged.emit(aggregations);
    }

    isFieldValidForAggregationType(field: string, aggregationType: string): boolean {
        switch (aggregationType) {
            case "COUNT":
                return field === "*";
            case "COUNT_DISTINCT":
                return field === "cont_id" || this.availableFields().some((f) => f.reportingFieldCode === field);
            case "SUM":
            case "AVG":
                return this.availableFields().some(
                    (f) =>
                        f.reportingFieldCode === field &&
                        (f.answerType === AnswerType.INTEGER || f.answerType === AnswerType.FLOAT),
                );
            default:
                return false;
        }
    }

    isAggregationComplete(aggregation: AggregationSpec): boolean {
        return this.isFieldValidForAggregationType(aggregation.field, aggregation.type);
    }
}
